import {
    TextField,
    Button,
    Box,
    Alert,
    AlertColor,
    AlertPropsVariantOverrides,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FILE_TYPES } from "../../assets/data/fileTypes";
import axiosInstance from "../../services/axios-instance";
import { FILES } from "../../services/endpoints";
import { multipartFormDataConfig } from "../../assets/data/multipartFormDataConfig";
import { FilesInterface } from "../../interface/filesInterface";
import { useState } from "react";
import { OverridableStringUnion } from "@mui/types";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as YUP from "yup";

const FileInput = styled("input")({
    display: "none",
});

interface UploadFormParams {
    allFiles: FilesInterface[];
    setAllFiles: React.Dispatch<React.SetStateAction<FilesInterface[]>>;
}

interface UploadFormValues {
    file: File | null;
    title: string;
    description: string;
    type: number | string;
}

const validationSchema = YUP.object({
    file: YUP.mixed().required("File is required"),
    title: YUP.string().required("Title is required"),
    description: YUP.string().required("Description is required"),
    type: YUP.string().required("Type is required"),
});

function UploadForm({ allFiles, setAllFiles }: UploadFormParams) {
    const [alertMessage, setAlertMessage] = useState("");
    const [severity, setSeverity] = useState<AlertColor | undefined>("info");
    const [variant, setVariant] = useState<
        | OverridableStringUnion<
              "standard" | "filled" | "outlined",
              AlertPropsVariantOverrides
          >
        | undefined
    >("filled");
    const [fileName, setFileName] = useState<string | null>(null);

    const handleSubmit = async (
        values: UploadFormValues,
        { resetForm }: any
    ) => {
        const { file, title, description, type } = values;

        const formData = new FormData();
        if (file) {
            formData.append("file", file);
        }

        formData.append("title", title);
        formData.append("description", description);
        formData.append("type", type?.toString() || "");

        try {
            const responseUploadFile = await axiosInstance.post(
                FILES,
                formData,
                multipartFormDataConfig
            );
            if (
                responseUploadFile.status === 200 ||
                responseUploadFile.status === 201
            ) {
                setSeverity("success");
                setAlertMessage("Document uploaded successfully");
                setVariant("filled");

                const newFile: FilesInterface = {
                    id: responseUploadFile.data.data.id,
                    title,
                    description,
                    type: typeof type === "number" ? type : 0,
                    file_path: responseUploadFile.data.file_path,
                };
                setAllFiles([...allFiles, newFile]);

                resetForm();
                setFileName("");
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setSeverity("error");
                setAlertMessage(error.response?.data.message);
                window.scrollTo(0, 0);
            }
        }
    };

    function getFileTypeIdsAndNames() {
        const keys = Object.keys(FILE_TYPES).filter((key) =>
            isNaN(Number(key))
        );
        return keys.map((key) => ({
            id: FILE_TYPES[key as keyof typeof FILE_TYPES],
            name: key,
        }));
    }

    const fileTypeIdsAndNames = getFileTypeIdsAndNames();

    return (
        <>
            <Box maxWidth="500px" mx="auto">
                <Formik
                    initialValues={{
                        file: null,
                        title: "",
                        description: "",
                        type: "",
                    }}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                >
                    {({ values, touched, errors, setFieldValue }) => (
                        <Form
                            noValidate
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                            }}
                        >
                            <FormGroup>
                                <label htmlFor="file">
                                    <Button
                                        variant="contained"
                                        component="span"
                                        color="info"
                                        sx={{ mr: "1rem" }}
                                    >
                                        Upload PDF File
                                    </Button>
                                    <Field
                                        as={FileInput}
                                        id="file"
                                        type="file"
                                        name="file"
                                        accept=".pdf"
                                        value=""
                                        onChange={(event: any) => {
                                            if (event.currentTarget.files[0]) {
                                                setFileName(
                                                    event.currentTarget.files[0]
                                                        .name
                                                );
                                                setFieldValue(
                                                    "file",
                                                    event.currentTarget.files[0]
                                                );
                                            }
                                        }}
                                    />
                                    {fileName && (
                                        <span>Selected file: {fileName}</span>
                                    )}
                                    <ErrorMessage
                                        name="file"
                                        component="span"
                                    />
                                </label>
                            </FormGroup>
                            <FormGroup>
                                <Field
                                    label="Title"
                                    name="title"
                                    as={TextField}
                                    error={
                                        touched.title && Boolean(errors.title)
                                    }
                                />
                                <ErrorMessage name="title" component="div" />
                            </FormGroup>
                            <FormGroup>
                                <Field
                                    label="Description"
                                    name="description"
                                    as={TextField}
                                    multiline
                                    error={
                                        touched.description &&
                                        Boolean(errors.description)
                                    }
                                />
                                <ErrorMessage
                                    name="description"
                                    component="div"
                                />
                            </FormGroup>

                            <FormControl
                                component="fieldset"
                                error={touched.type && Boolean(errors.type)}
                            >
                                <FormLabel component="legend">
                                    You can add files and documents of the
                                    following types:
                                </FormLabel>
                                <RadioGroup
                                    aria-label="fileType"
                                    name="fileType"
                                    value={values.type}
                                    onChange={(event) => {
                                        setFieldValue(
                                            "type",
                                            event.currentTarget.value
                                        );
                                    }}
                                >
                                    {fileTypeIdsAndNames.map((object) => (
                                        <FormControlLabel
                                            key={object.id}
                                            value={object.id}
                                            control={<Radio />}
                                            label={object.name}
                                        />
                                    ))}
                                </RadioGroup>
                                <ErrorMessage name="type" component="div" />
                            </FormControl>
                            <Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="info"
                                >
                                    Submit
                                </Button>
                            </Box>
                            {alertMessage != "" && (
                                <Alert
                                    onClick={() => setAlertMessage("")}
                                    variant={variant}
                                    severity={severity}
                                >
                                    {alertMessage}
                                </Alert>
                            )}
                        </Form>
                    )}
                </Formik>
            </Box>
        </>
    );
}

export default UploadForm;
